import * as React from 'react';
import { AssignDevicesStore } from 'store/assign-devices-store';
import { DangerTag, IconTextButton, Progress, SuccessTag, Typography, WarningTag, showConfirmDialog } from 'ui-lib';
import { IImportDeviceProps, MoveDeviceStatus } from 'dto/device-dto';
import { getErrorMessage } from 'common-utils';
import { AssignDeviceSummaryTable } from './assign-devices-summary-table';
import { observer } from 'mobx-react';
import { useDeviceGroupPermissionContext } from 'contexts/permissions/device-group-permission.context';

export interface IMoveDevicesSummaryProps {
    assignDeviceStore: AssignDevicesStore;
    deviceGroupName?: string;
    onSave?: () => Promise<unknown>;
    onClose: () => void;
    redirectToDeploymentPage?: () => void;
    redirectToDeviceGroupsTable: () => void;
}

enum ModalState {
    PREVIEW = 'PREVIEW',
    PROGRESS = 'PROGRESS',
    FINISH = 'FINISH'
}

const MOVE_DEVICE_STATUSES = Object.values(MoveDeviceStatus);

const isProgress = (state: ModalState): boolean => state === ModalState.PROGRESS;

export const MoveDevicesSummaryTable = observer((props: IMoveDevicesSummaryProps): JSX.Element => {
    const { runHandler } = useDeviceGroupPermissionContext();
    const { assignDeviceStore, deviceGroupName } = props;
    const [deviceDataSource, setDeviceDataSource] = React.useState<IImportDeviceProps[]>([]);

    const [modalState, setModalState] = React.useReducer((_, state: ModalState): ModalState => {
        if (state === ModalState.PROGRESS) {
            assignDeviceStore.updateProgressPercent(0);
        }
        return state;
    }, ModalState.PREVIEW);

    React.useEffect(() => {
        setDeviceDataSource(
            [
                ...assignDeviceStore.toClientGroupDevicesList.map(item => ({...item, status: MoveDeviceStatus.REMOVE_FROM_GROUP})),
                ...assignDeviceStore.toSelectedGroupDevicesList.map(item => ({...item, status: MoveDeviceStatus.ADD_TO_GROUP})),
            ]
        );
    }, []);

    React.useEffect(() => {
        if (!isProgress(modalState)) {
            return;
        }
        setDeviceDataSource((dataSource) => {
            return dataSource.map((item) => ({ ...item, status: MoveDeviceStatus.UPDATING }));
        });

        (async (): Promise<void> => {
            assignDeviceStore.setLocked(true);
            try {
                if (props.onSave) {
                    await props.onSave();
                }
                await runHandler('Move Devices', async () => {
                    try {
                        const result = await assignDeviceStore.updateDeviceGroupForDevices(deviceGroupName);
                        const devices = assignDeviceStore.displayDeviceDataSource
                                .filter((item) => deviceDataSource.some((device) => device.UID === item.UID));
                        setDeviceDataSource((source) => {
                            return devices
                                .map((item) => {
                                    if (result.succeeded.includes(item.UID)) {
                                        return { ...item, status: MoveDeviceStatus.UPDATED };
                                    }

                                    const found = result.errors.find((err) => err.identifier === item.UID);
                                    if (found) {
                                        return { ...item, status: MoveDeviceStatus.ERROR, comment: found.error };
                                    }
                                    return item;
                                });
                        });
                    } catch (err) {
                        const errMsg = getErrorMessage(err);
                        setDeviceDataSource((source) =>
                            source.map((item) => ({ ...item, status: MoveDeviceStatus.ERROR, comment: errMsg })),
                        );
                    } finally {
                        setModalState(ModalState.FINISH);
                    }
                });
            } finally {
                assignDeviceStore.setLocked(false);
            }
        })();
    }, [modalState]);

    const extraItems: React.ReactNode[] = [];
    if (modalState === ModalState.PREVIEW) {
        extraItems.push(<IconTextButton
            key='update-btn'
            label='Update'
            onClick={(): void => showConfirmDialog({
                title: 'Confirmation',
                content: `Are you sure to update ${deviceDataSource.length} devices?`,
                okText: 'Yes',
                onOk: () => setModalState(ModalState.PROGRESS),
            })}
            iconName='plus'
            type='primary'
        />);
    } else if (modalState === ModalState.PROGRESS) {
        extraItems.push(<Progress
            key='progress'
            percent={assignDeviceStore.progressPercent}
            type='circle'
            extraProps={{ width: 40 }}
        />);
    } else {
        extraItems.push(null);
    }
    extraItems.push(<IconTextButton
        key='import-btn'
        label='Close'
        disabled={isProgress(modalState)}
        onClick={(): void => {
            if (modalState === ModalState.FINISH) {
                props.redirectToDeviceGroupsTable();
            }
            props.onClose();
        }}
        iconName='cancel'
        type='default'
    />);

    return (
        <React.Fragment>
            <AssignDeviceSummaryTable
                clientAccountId={assignDeviceStore.clientAccount.accountId}
                header={<Typography.Title level={3}>Update devices</Typography.Title>}
                title={`Updating ${deviceDataSource.length} Devices`}
                dataSource={deviceDataSource}
                statusColumn={{
                    filterKeys: MOVE_DEVICE_STATUSES,
                    renderTag: (text: string): React.ReactNode => {
                        if (text === MoveDeviceStatus.ERROR) {
                            return <DangerTag value={text} />;
                        } else if (text === MoveDeviceStatus.REMOVE_FROM_GROUP) {
                            return <WarningTag value={text} />;
                        }
                        return <SuccessTag value={text} />;
                    },
                }}
                extraItems={extraItems}
            />
            <IconTextButton
                hidden={!props.redirectToDeploymentPage || modalState !== ModalState.FINISH}
                key='navigate-btn'
                label='Go to Deployments'
                onClick={props.redirectToDeploymentPage}
                type='default'
            />
        </React.Fragment>
    );
});
